import { lazy, type ComponentType } from 'react'

/**
 * Lazy load component with retry logic to handle dynamic import failures
 * This helps resolve "Failed to fetch dynamically imported module" errors
 *
 * @param componentImport - The dynamic import function
 * @param retries - Number of retry attempts (default: 3)
 * @param interval - Delay between retries in ms (default: 1000)
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 3,
  interval = 1000
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const fetchComponent = async (
      retriesLeft = retries
    ): Promise<{ default: T }> => {
      try {
        return await componentImport()
      } catch (error) {
        // If we've exhausted retries, throw the error
        if (retriesLeft === 0) {
          console.error('Failed to load component after retries:', error)

          // Check if it's a chunk loading error
          if (
            error instanceof TypeError &&
            error.message.includes('Failed to fetch')
          ) {
            // Reload the page to get fresh chunks
            console.warn('Reloading page to fetch fresh chunks...')
            window.location.reload()
          }

          throw error
        }

        console.warn(
          `Failed to load component. Retrying... (${retries - retriesLeft + 1}/${retries})`,
          error
        )

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, interval))

        // Retry
        return fetchComponent(retriesLeft - 1)
      }
    }

    return fetchComponent()
  })
}
